import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { ScrollArea } from '../ui/scroll-area';
import { ChevronRight, FileText, Folder } from 'lucide-react';

export function DocsSidebar({ project, docs, categories }) {
    const location = useLocation();
    
    // Filter out translations (only show original docs, not translated versions)
    const originalDocs = docs.filter(doc => !doc.translation_of);
    
    // Group docs by category
    const groupedDocs = originalDocs.reduce((acc, doc) => {
        const cat = doc.category || 'Uncategorized';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(doc);
        return acc;
    }, {});

    // Sort categories by order
    const sortedCategories = Object.keys(groupedDocs).sort((a, b) => {
        const catA = categories.find(c => c.name === a);
        const catB = categories.find(c => c.name === b);
        return (catA?.order || 0) - (catB?.order || 0);
    });

    return (
        <aside className="w-64 border-r border-border bg-card/50 hidden lg:block">
            <ScrollArea className="h-[calc(100vh-80px)] py-6 px-4">
                {/* Project Header */}
                <div className="mb-6">
                    <Link 
                        to={`/docs/${project.slug}`}
                        className="flex items-center gap-2.5 font-display font-bold text-lg hover:text-primary transition-colors"
                        data-testid="sidebar-project-link"
                    >
                        {project.icon ? (
                            <span className="text-2xl">{project.icon}</span>
                        ) : (
                            <div 
                                className="w-6 h-6 rounded-md"
                                style={{ backgroundColor: project.color }}
                            />
                        )}
                        {project.name}
                    </Link>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {project.description}
                    </p>
                </div>

                {/* Navigation */}
                <nav className="space-y-4">
                    {sortedCategories.map(category => (
                        <div key={category}>
                            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
                                <Folder className="w-3 h-3" />
                                {category}
                            </div>
                            <ul className="space-y-1">
                                {groupedDocs[category]
                                    .sort((a, b) => a.order - b.order)
                                    .map(doc => {
                                        const isActive = location.pathname === `/docs/${project.slug}/${doc.slug}`;
                                        return (
                                            <li key={doc.id}>
                                                <Link
                                                    to={`/docs/${project.slug}/${doc.slug}`}
                                                    className={cn(
                                                        "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all",
                                                        isActive 
                                                            ? "bg-primary/10 text-primary border-l-2 border-primary" 
                                                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                                    )}
                                                    data-testid={`sidebar-doc-${doc.slug}`}
                                                >
                                                    <FileText className="w-4 h-4 flex-shrink-0" />
                                                    <span className="truncate">{doc.title}</span>
                                                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                                                </Link>
                                            </li>
                                        );
                                    })}
                            </ul>
                        </div>
                    ))}
                </nav>

                {/* Back to all projects */}
                <div className="mt-8 pt-4 border-t border-border">
                    <Link 
                        to="/"
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="back-to-projects"
                    >
                        <ChevronRight className="w-4 h-4 rotate-180" />
                        Alle Projekte
                    </Link>
                </div>
            </ScrollArea>
        </aside>
    );
}
